import api from "./api";
import { getApiUrl } from "./config";
import { handleApiCall } from "../utils/apiResponseHandler";
import { endpoint } from "./endpoints";

// ============ TYPES ============

export type AgentApiAccessStatus =
  | "pending"
  | "approved"
  | "active"
  | "denied"
  | "suspended"
  | "revoked";

export interface AgentApiAccess {
  id: string;
  userId: string;
  agencyId?: string;
  businessName: string;
  websiteUrl?: string;
  intendedUse: string;
  expectedVolume?: string;
  apiKeyPrefix?: string;
  apiKeyCreatedAt?: string;
  status: AgentApiAccessStatus;
  reviewedByUserId?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  denialReason?: string;
  rateLimitPerMinute: number;
  allowedIps?: string[];
  totalLeadsSubmitted: number;
  totalLeadsSuccess: number;
  totalLeadsFailed: number;
  lastApiCallAt?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  agency?: {
    id: string;
    name: string;
  };
  reviewedByUser?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface AgentApiLeadSubmission {
  id: string;
  agentApiAccessId?: string;
  userId?: string;
  createdLeadId?: string;
  leadId?: string; // Alias for createdLeadId from backend
  externalLeadId?: string;
  leadName?: string; // Lead's full name
  leadPhone?: string; // Lead's phone number
  leadEmail?: string; // Lead's email
  status: "pending" | "success" | "failed" | "duplicate";
  errorMessage?: string;
  rawPayload?: Record<string, unknown>;
  requestIp?: string;
  processedAt?: string;
  createdAt: string;
}

export interface AgentApiStats {
  totalLeadsSubmitted: number;
  totalLeadsSuccess: number;
  totalLeadsFailed: number;
  last30Days: {
    submitted: number;
    success: number;
    failed: number;
  };
}

export interface RequestApiAccessParams {
  businessName: string;
  websiteUrl?: string;
  intendedUse: string;
  expectedVolume?: string;
}

export interface ApiDocs {
  baseUrl: string;
  authentication: {
    header: string;
    format: string;
    example: string;
  };
  endpoints: {
    submitLeads: {
      method: string;
      path: string;
      description: string;
      requestBody: {
        leads: {
          type: string;
          maxItems: number;
          required: string[];
          optional: string[];
        };
      };
      response: {
        results: unknown[];
        summary: {
          total: number;
          success: number;
          failed: number;
          duplicates: number;
        };
      };
    };
  };
  rateLimiting: {
    perMinute: number;
    description: string;
  };
  ipWhitelist?: {
    enabled: boolean;
    ips: string[];
  };
}

// ============ SECURITY TYPES ============

export interface SecurityAlert {
  type: 'suspicious_ip' | 'rate_limit_exceeded' | 'invalid_api_key_attempts' | 'ip_blocked' | 'ip_change' | 'burst_activity';
  message: string;
  details: Record<string, unknown>;
  createdAt: string;
}

export interface BlockedIp {
  ip: string;
  until: string;
  reason: string;
}

export interface FailedAttemptStats {
  ip: string;
  stats: {
    count: number;
    lastAttempt: string;
    apiKeyPrefixes: string[];
  };
}

export interface AgentApiLog {
  id: string;
  agentApiAccessId?: string;
  userId?: string;
  endpoint: string;
  method: string;
  requestIp?: string;
  responseStatus?: number;
  responseTimeMs?: number;
  leadsCount?: number;
  successCount?: number;
  failedCount?: number;
  duplicateCount?: number;
  eventType: string;
  eventDetails?: Record<string, unknown>;
  createdAt: string;
}

export interface SecurityAlertsResponse {
  alerts: Array<{ accessId: string; alerts: SecurityAlert[] }>;
  failedAttempts: FailedAttemptStats[];
  blockedIps: BlockedIp[];
  summary: {
    totalAccessesWithAlerts: number;
    totalFailedAttemptIps: number;
    totalBlockedIps: number;
  };
}

export interface UsageAnalytics {
  totalRequests: number;
  totalLeads: number;
  successfulLeads: number;
  failedLeads: number;
  duplicateLeads: number;
  uniqueIps: number;
  dailyBreakdown: Array<{ date: string; requests: number; leads: number }>;
}

// ============ AGENT SERVICE FUNCTIONS ============

class AgentApiService {
  /**
   * Get current API access status for the logged-in agent
   */
  async getStatus(): Promise<{ access: AgentApiAccess | null; hasAccess: boolean }> {
    const url = getApiUrl(endpoint.agentApi.status);

    try {
      const response = await api.get({ url, auth: true });
      // Backend response structure: { statusCode, message, data: { statusCode, message, data: { hasAccess, status, ... } } }
      // Axios interceptor returns response.data, so we get the outer wrapper
      // The actual data is in response.data.data (controller response nested inside)
      const outerData = (response as any)?.data || response;
      const data = outerData?.data || outerData;

      // Only return null if there's truly no access request (status is null/undefined)
      // Don't return null for pending/denied/suspended states - we want to show those
      if (!data || data.status === null || data.status === undefined) {
        return { access: null, hasAccess: false };
      }

      // Map the backend response to our frontend type
      const access: AgentApiAccess = {
        id: data.id || '',
        userId: data.userId || '',
        agencyId: data.agencyId,
        businessName: data.businessName || '',
        websiteUrl: data.websiteUrl,
        intendedUse: data.intendedUse || '',
        expectedVolume: data.expectedVolume,
        apiKeyPrefix: data.apiKeyPrefix,
        apiKeyCreatedAt: data.apiKeyCreatedAt,
        status: data.status,
        reviewedByUserId: data.reviewedByUserId,
        reviewedAt: data.reviewedAt,
        reviewNotes: data.reviewNotes,
        denialReason: data.denialReason,
        rateLimitPerMinute: data.rateLimitPerMinute || 60,
        allowedIps: data.allowedIps,
        totalLeadsSubmitted: data.stats?.totalLeadsSubmitted || data.totalLeadsSubmitted || 0,
        totalLeadsSuccess: data.stats?.totalLeadsSuccess || data.totalLeadsSuccess || 0,
        totalLeadsFailed: data.stats?.totalLeadsFailed || data.totalLeadsFailed || 0,
        lastApiCallAt: data.lastApiCallAt,
        createdAt: data.createdAt || '',
        updatedAt: data.updatedAt || data.createdAt || '',
      };

      return { access, hasAccess: data.hasAccess || data.status === 'active' };
    } catch (error) {
      console.error('Failed to get API access status:', error);
      return { access: null, hasAccess: false };
    }
  }

  /**
   * Request API access (creates a pending request for admin approval)
   */
  async requestAccess(params: RequestApiAccessParams): Promise<{ access: AgentApiAccess }> {
    const url = getApiUrl(endpoint.agentApi.request);

    return handleApiCall(async () => {
      return api.post({ url, body: params, auth: true });
    });
  }

  /**
   * Get usage statistics for the agent's API access
   */
  async getStats(): Promise<{ stats: AgentApiStats }> {
    const url = getApiUrl(endpoint.agentApi.stats);

    return handleApiCall(async () => {
      return api.get({ url, auth: true });
    });
  }

  /**
   * Get lead submissions history
   */
  async getSubmissions(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ submissions: AgentApiLeadSubmission[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const url = getApiUrl(
      `${endpoint.agentApi.submissions}${queryString ? `?${queryString}` : ""}`
    );

    const result = await handleApiCall<{ submissions: AgentApiLeadSubmission[]; total: number }>(
      async () => {
        return api.get({ url, auth: true });
      }
    );
    return result || { submissions: [], total: 0 };
  }

  /**
   * Regenerate API key (returns new key only once - must be saved by user)
   */
  async regenerateApiKey(): Promise<{
    access: AgentApiAccess;
    apiKey: string;
    warning: string;
  }> {
    const url = getApiUrl(endpoint.agentApi.regenerateKey);

    return handleApiCall(async () => {
      return api.post({ url, body: {}, auth: true });
    });
  }

  /**
   * Revoke API access (self-service revocation)
   */
  async revokeAccess(): Promise<{ access: AgentApiAccess; message: string }> {
    const url = getApiUrl(endpoint.agentApi.revoke);

    return handleApiCall(async () => {
      return api.post({ url, body: {}, auth: true });
    });
  }

  /**
   * Get API documentation and integration guide
   */
  async getDocs(): Promise<{ docs: ApiDocs }> {
    const url = getApiUrl(endpoint.agentApi.docs);

    return handleApiCall(async () => {
      return api.get({ url, auth: true });
    });
  }
}

// ============ ADMIN SERVICE FUNCTIONS ============

class AgentApiAdminService {
  /**
   * Get count of pending requests (for admin badge)
   */
  async getPendingCount(): Promise<{ count: number }> {
    const url = getApiUrl(endpoint.agentApi.admin.pendingCount);

    const result = await handleApiCall<{ count: number }>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { count: 0 };
  }

  /**
   * List all API access requests with optional filtering
   */
  async listRequests(params?: {
    status?: AgentApiAccessStatus | AgentApiAccessStatus[];
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: AgentApiAccess[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.status) {
      if (Array.isArray(params.status)) {
        params.status.forEach((s) => queryParams.append("status", s));
      } else {
        queryParams.append("status", params.status);
      }
    }
    if (params?.search) queryParams.append("search", params.search);
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const url = getApiUrl(
      `${endpoint.agentApi.admin.requests}${queryString ? `?${queryString}` : ""}`
    );

    const result = await handleApiCall<{ items: AgentApiAccess[]; total: number }>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { items: [], total: 0 };
  }

  /**
   * Approve a pending request (returns API key only once)
   */
  async approveRequest(
    id: string,
    notes?: string
  ): Promise<{ access: AgentApiAccess; apiKey: string }> {
    const url = getApiUrl(endpoint.agentApi.admin.approve(id));

    return handleApiCall(async () => {
      return api.post({ url, body: { notes }, auth: true });
    });
  }

  /**
   * Deny a pending request
   */
  async denyRequest(id: string, reason: string): Promise<{ access: AgentApiAccess }> {
    const url = getApiUrl(endpoint.agentApi.admin.deny(id));

    return handleApiCall(async () => {
      return api.post({ url, body: { reason }, auth: true });
    });
  }

  /**
   * Suspend active access
   */
  async suspendAccess(id: string, reason?: string): Promise<{ access: AgentApiAccess }> {
    const url = getApiUrl(endpoint.agentApi.admin.suspend(id));

    return handleApiCall(async () => {
      return api.post({ url, body: { reason }, auth: true });
    });
  }

  /**
   * Reactivate suspended access
   */
  async reactivateAccess(id: string): Promise<{ access: AgentApiAccess }> {
    const url = getApiUrl(endpoint.agentApi.admin.reactivate(id));

    return handleApiCall(async () => {
      return api.post({ url, body: {}, auth: true });
    });
  }

  /**
   * Revoke access (admin action)
   */
  async revokeAccess(id: string): Promise<{ access: AgentApiAccess }> {
    const url = getApiUrl(endpoint.agentApi.admin.revoke(id));

    return handleApiCall(async () => {
      return api.post({ url, body: {}, auth: true });
    });
  }

  /**
   * Regenerate API key for an agent (admin action)
   */
  async regenerateApiKey(id: string): Promise<{
    access: AgentApiAccess;
    apiKey: string;
    warning: string;
  }> {
    const url = getApiUrl(endpoint.agentApi.admin.regenerateKey(id));

    return handleApiCall(async () => {
      return api.post({ url, body: {}, auth: true });
    });
  }

  /**
   * Get submissions for a specific agent's API access
   */
  async getSubmissions(
    id: string,
    params?: { status?: string; limit?: number; offset?: number }
  ): Promise<{ submissions: AgentApiLeadSubmission[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const url = getApiUrl(
      `${endpoint.agentApi.admin.getSubmissions(id)}${queryString ? `?${queryString}` : ""}`
    );

    const result = await handleApiCall<{ submissions: AgentApiLeadSubmission[]; total: number }>(
      async () => {
        return api.get({ url, auth: true });
      }
    );
    return result || { submissions: [], total: 0 };
  }

  // ============ SECURITY METHODS ============

  /**
   * Get all security alerts across all agent API accesses
   */
  async getSecurityAlerts(): Promise<SecurityAlertsResponse> {
    const url = getApiUrl(endpoint.agentApi.admin.securityAlerts);

    const result = await handleApiCall<SecurityAlertsResponse>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { alerts: [], failedAttempts: [], blockedIps: [], summary: { totalAccessesWithAlerts: 0, totalFailedAttemptIps: 0, totalBlockedIps: 0 } };
  }

  /**
   * Get security event logs
   */
  async getSecurityEvents(params?: {
    types?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ logs: AgentApiLog[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.types) queryParams.append("types", params.types);
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const url = getApiUrl(
      `${endpoint.agentApi.admin.securityEvents}${queryString ? `?${queryString}` : ""}`
    );

    const result = await handleApiCall<{ logs: AgentApiLog[]; total: number }>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { logs: [], total: 0 };
  }

  /**
   * Get blocked IPs
   */
  async getBlockedIps(): Promise<{ blockedIps: BlockedIp[]; count: number }> {
    const url = getApiUrl(endpoint.agentApi.admin.blockedIps);

    const result = await handleApiCall<{ blockedIps: BlockedIp[]; count: number }>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { blockedIps: [], count: 0 };
  }

  /**
   * Manually block an IP
   */
  async blockIp(ip: string, durationMinutes: number, reason: string): Promise<{
    ip: string;
    blockedUntil: string;
    reason: string;
  }> {
    const url = getApiUrl(endpoint.agentApi.admin.blockIp);

    return handleApiCall(async () => {
      return api.post({ url, body: { ip, durationMinutes, reason }, auth: true });
    });
  }

  /**
   * Unblock an IP
   */
  async unblockIp(ip: string): Promise<{ ip: string }> {
    const url = getApiUrl(endpoint.agentApi.admin.unblockIp(ip));

    return handleApiCall(async () => {
      return api.delete({ url, auth: true });
    });
  }

  /**
   * Get logs by IP address
   */
  async getLogsByIp(
    ip: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{ ip: string; logs: AgentApiLog[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const url = getApiUrl(
      `${endpoint.agentApi.admin.logsByIp(ip)}${queryString ? `?${queryString}` : ""}`
    );

    const result = await handleApiCall<{ ip: string; logs: AgentApiLog[]; total: number }>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { ip, logs: [], total: 0 };
  }

  /**
   * Get security alerts for a specific access
   */
  async getAccessSecurity(id: string): Promise<{
    alerts: SecurityAlert[];
    recentSecurityEvents: AgentApiLog[];
    totalSecurityEvents: number;
  }> {
    const url = getApiUrl(endpoint.agentApi.admin.getAccessSecurity(id));

    const result = await handleApiCall<{
      alerts: SecurityAlert[];
      recentSecurityEvents: AgentApiLog[];
      totalSecurityEvents: number;
    }>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { alerts: [], recentSecurityEvents: [], totalSecurityEvents: 0 };
  }

  /**
   * Get API logs for a specific access
   */
  async getAccessLogs(
    id: string,
    params?: { eventType?: string; limit?: number; offset?: number }
  ): Promise<{ logs: AgentApiLog[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.eventType) queryParams.append("eventType", params.eventType);
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const url = getApiUrl(
      `${endpoint.agentApi.admin.getAccessLogs(id)}${queryString ? `?${queryString}` : ""}`
    );

    const result = await handleApiCall<{ logs: AgentApiLog[]; total: number }>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { logs: [], total: 0 };
  }

  /**
   * Get usage analytics for a specific access
   */
  async getAccessAnalytics(id: string, days?: number): Promise<UsageAnalytics> {
    const queryParams = new URLSearchParams();
    if (days) queryParams.append("days", String(days));

    const queryString = queryParams.toString();
    const url = getApiUrl(
      `${endpoint.agentApi.admin.getAccessAnalytics(id)}${queryString ? `?${queryString}` : ""}`
    );

    const result = await handleApiCall<UsageAnalytics>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { totalRequests: 0, totalLeads: 0, successfulLeads: 0, failedLeads: 0, duplicateLeads: 0, uniqueIps: 0, dailyBreakdown: [] };
  }

  /**
   * Update API access settings (rate limit, allowed IPs)
   */
  async updateSettings(
    id: string,
    settings: { rateLimitPerMinute?: number; allowedIps?: string[] }
  ): Promise<{ access: AgentApiAccess }> {
    const url = getApiUrl(endpoint.agentApi.admin.updateSettings(id));

    return handleApiCall(async () => {
      return api.patch({ url, body: settings, auth: true });
    });
  }
}

// Export singleton instances
export const agentApiService = new AgentApiService();
export const agentApiAdminService = new AgentApiAdminService();

// Default export for backwards compatibility
export default {
  agent: agentApiService,
  admin: agentApiAdminService,
};
